
import { useState } from 'react';
import SEOHead from '../../components/SEOHead';

export default function Home() {
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [consultationFormData, setConsultationFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'residential',
    message: ''
  });
  const [messageFormData, setMessageFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleConsultationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formData = new URLSearchParams();
      Object.entries(consultationFormData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d367oi2b9bj1s07cntk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      if (response.ok) {
        setSubmitStatus('success');
        setConsultationFormData({ name: '', email: '', phone: '', service: 'residential', message: '' });
        setTimeout(() => {
          setIsConsultationModalOpen(false);
          setSubmitStatus('');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMessageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formData = new URLSearchParams();
      Object.entries(messageFormData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d367oi2b9bj1s07cntkg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      if (response.ok) {
        setSubmitStatus('success');
        setMessageFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setTimeout(() => {
          setIsMessageModalOpen(false);
          setSubmitStatus('');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "Tony Monteiro",
    "jobTitle": "Sales Representative",
    "worksFor": {
      "@type": "RealEstateAgency",
      "name": "Royal LePage Crown Realty Services",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "4-471 Hespeler Road",
        "addressLocality": "Cambridge",
        "addressRegion": "ON",
        "postalCode": "N1R 6J2",
        "addressCountry": "CA"
      },
      "telephone": "(519) 658-3026",
      "faxNumber": "(519) 740-6403"
    },
    "telephone": "(519) 658-3026",
    "url": import.meta.env.VITE_SITE_URL,
    "image": "https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/54d5be67d9842835a1a500aee6ea8ce2.jfif",
    "description": "Trusted Cambridge-based realtor with 35+ years of experience. Tony Monteiro specializes in residential, multi-family, and commercial real estate across Waterloo Region.",
    "areaServed": [
      "Waterloo",
      "Puslinch", 
      "New Hamburg",
      "New Dundee",
      "K-W",
      "Cambridge",
      "Baden",
      "Ayr"
    ],
    "knowsLanguage": ["Portuguese", "English"],
    "award": ["Red Diamond (2021-22)", "Diamond Award 2024", "Ruby Award 2023", "Diamond (2019-20)"],
    "serviceType": ["Residential Real Estate", "Multi-Family Properties", "Commercial Real Estate", "Investment Properties"],
    "hasCredential": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "Real Estate License",
      "recognizedBy": {
        "@type": "Organization",
        "name": "Royal LePage Crown Realty Services"
      }
    }
  };

  return (
    <>
      <SEOHead
        title="Tony Monteiro | Real Estate in Cambridge ON"
        description="Trusted Cambridge-based realtor with 25+ years of experience. Tony Monteiro specializes in residential, multi-family, and commercial real estate across Waterloo Region. Book your free consultation today."
        keywords="real estate agent Cambridge Ontario, Waterloo realtor, Tony Monteiro, Royal LePage Crown Realty, residential real estate, commercial properties, multi-family homes, property investment"
        canonicalUrl={import.meta.env.VITE_SITE_URL}
        ogImage="https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/c02b36e925f868886ab5c4518604dda3.png"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen bg-slate-900 text-white">
        {/* Header */}
        <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-2 sm:space-x-4 mb-4">
                <div className="bg-white rounded-lg p-2">
                  <img 
                    src="https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/41ba4e2750ccf943b2442aa568081ca1.png"
                    alt="Royal LePage Logo"
                    className="h-8 sm:h-12 w-auto object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold">Tony Monteiro</h3>
                  <p className="text-xs sm:text-sm text-slate-400">Sales Representative</p>
                </div>
              </div>
              <nav className="hidden md:flex space-x-8">
                <a href="#home" className="text-white hover:text-red-400 transition-colors cursor-pointer">Home</a>
                <a href="#services" className="text-slate-300 hover:text-red-400 transition-colors cursor-pointer">Services</a>
                <a href="#about" className="text-slate-300 hover:text-red-400 transition-colors cursor-pointer">About</a>
                <a href="#contact" className="text-slate-300 hover:text-red-400 transition-colors cursor-pointer">Contact</a>
              </nav>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section id="home" className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
            style={{
              backgroundImage: `url('https://public.readdy.ai/ai/img_res/726e03da80d742ea325919530425e1a4.jpg')`
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-800/70"></div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
                  Your Trusted Partner in{' '}
                  <span className="text-red-500">Real Estate</span>
                </h1>
                <p className="text-lg sm:text-xl text-slate-300 mb-6 sm:mb-8 leading-relaxed px-2 sm:px-0">
                  Residential, Multi-Family & Commercial Solutions with 35 Years of Experience.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start px-4 sm:px-0">
                  <a
                    href="https://calendly.com/tjcmonteiro/free-mortgage-consultation-with-tony-monteiro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-red-600 hover:bg-red-700 text-white px-12 sm:px-16 py-3 sm:py-4 rounded-lg font-semibold transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer text-sm sm:base inline-flex items-center justify-center min-w-[280px] sm:min-w-[320px]"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Schedule a Call
                  </a>
                </div>

                <div className="flex justify-center lg:justify-start space-x-6 sm:space-x-8 mt-8 sm:mt-12 text-center">
                  <div>
                    <div className="text-red-500 font-bold text-xl sm:text-2xl">Licensed</div>
                    <div className="text-slate-400 text-xs sm:text-sm">Sales Rep</div>
                  </div>
                  <div>
                    <div className="text-red-500 font-bold text-xl sm:text-2xl">35+</div>
                    <div className="text-slate-400 text-xs sm:text-sm">Years</div>
                  </div>
                  <div>
                    <div className="text-red-500 font-bold text-xl sm:text-2xl">Expert</div>
                    <div className="text-slate-400 text-xs sm:text-sm">Service</div>
                  </div>
                </div>
              </div>

              <div className="relative mt-8 lg:mt-0">
                <img
                  src="https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/54d5be67d9842835a1a500aee6ea8ce2.jfif"
                  alt="Tony Monteiro - Sales Representative"
                  className="w-full max-w-sm lg:max-w-md mx-auto rounded-2xl shadow-2xl object-cover object-top"
                />
                <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-red-600 text-white p-3 sm:p-4 rounded-xl shadow-lg">
                  <div className="text-xs sm:text-sm font-semibold">Sales Representative</div>
                  <div className="text-xs opacity-90">Ready to Help</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">My <span className="text-red-500">Services</span></h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Comprehensive real estate solutions tailored to your unique needs
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-slate-900/50 p-8 rounded-xl border border-slate-700 hover:border-red-500/50 transition-all group cursor-pointer">
                <div className="w-16 h-16 bg-red-600/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-600/30 transition-colors">
                  <i className="ri-home-4-line text-2xl text-red-500"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-red-400 transition-colors">Residential</h3>
                <p className="text-slate-300 leading-relaxed">
                  Expert guidance for buying and selling homes and condos. From first-time buyers to luxury properties, I provide personalized service every step of the way.
                </p>
              </div>

              <div className="bg-slate-900/50 p-8 rounded-xl border border-slate-700 hover:border-red-500/50 transition-all group cursor-pointer">
                <div className="w-16 h-16 bg-red-600/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-600/30 transition-colors">
                  <i className="ri-building-line text-2xl text-red-500"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-red-400 transition-colors">Multi-Family</h3>
                <p className="text-slate-300 leading-relaxed">
                  Specialized support for investors and rental property owners. Maximize your investment potential with strategic guidance and market insights.
                </p>
              </div>

              <div className="bg-slate-900/50 p-8 rounded-xl border border-slate-700 hover:border-red-500/50 transition-all group cursor-pointer">
                <div className="w-16 h-16 bg-red-600/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-600/30 transition-colors">
                  <i className="ri-building-2-line text-2xl text-red-500"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-red-400 transition-colors">Commercial</h3>
                <p className="text-slate-300 leading-relaxed">
                  Helping businesses and developers grow with commercial real estate solutions. From retail spaces to development opportunities.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">
                  <span className="text-red-500">35 Years</span> of Real Estate Experience
                </h2>
                <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                  Throughout my career in real estate, I have built a reputation for providing exceptional, personalized service backed by the strength and resources of Royal LePage Crown Realty Services. My commitment to excellence and deep market knowledge has helped countless clients achieve their real estate goals.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-6 mb-8">
                  <div className="bg-slate-800/50 p-6 rounded-lg border border-slate-700">
                    <h4 className="font-bold text-lg mb-3 text-red-400">Languages</h4>
                    <div className="flex flex-col space-y-2">
                      <span className="flex items-center text-sm"><i className="ri-global-line mr-2 w-4 h-4 flex items-center justify-center"></i> Portuguese</span>
                      <span className="flex items-center text-sm"><i className="ri-global-line mr-2 w-4 h-4 flex items-center justify-center"></i> English</span>
                    </div>
                  </div>
                  <div className="bg-slate-800/50 p-6 rounded-lg border border-slate-700">
                    <h4 className="font-bold text-lg mb-2 text-red-400">Awards</h4>
                    <div className="space-y-1 text-sm">
                      <div>Red Diamond (2021-22)</div>
                      <div>Diamond Award 2024</div>
                      <div>Ruby Award 2023</div>
                      <div>Diamond (2019-20)</div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/30 p-6 rounded-lg border border-slate-700">
                  <h4 className="font-bold text-lg mb-3 text-red-400">Areas Serviced</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                    <span>Waterloo</span>
                    <span>Puslinch</span>
                    <span>New Hamburg</span>
                    <span>New Dundee</span>
                    <span>K-W</span>
                    <span>Cambridge</span>
                    <span>Baden</span>
                    <span>Ayr</span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20real%20estate%20office%20workspace%20with%20awards%20and%20certificates%20on%20wall%2C%20professional%20business%20environment%2C%20dark%20navy%20blue%20theme%2C%20elegant%20desk%20setup%2C%20property%20listings%20displayed%2C%20sophisticated%20interior%20design&width=600&height=700&seq=office-workspace-001&orientation=portrait"
                  alt="Professional Real Estate Office"
                  className="w-full rounded-2xl shadow-2xl object-cover object-top"
                />
                <div className="absolute top-6 right-6 bg-slate-900/90 p-4 rounded-lg backdrop-blur-sm">
                  <div className="text-red-500 font-bold text-lg">Award Winner</div>
                  <div className="text-sm text-slate-300">Excellence in Service</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Expertise Section */}
        <section className="py-20 bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">My <span className="text-red-500">Expertise</span></h2>
              <p className="text-xl text-slate-300">Specialized knowledge across all property types</p>
            </div>

            {/* Marquee Container */}
            <div className="relative overflow-hidden bg-slate-900 rounded-xl py-8">
              <div className="marquee-container">
                <div className="marquee-content">
                  <span className="marquee-text">
                    Condos • Rentals • Relocations • First-Time Buyers • Carriage Trade • New Homes • Multi-Residential • Investment • 
                    Condos • Rentals • Relocations • First-Time Buyers • Carriage Trade • New Homes • Multi-Residential • Investment
                  </span>
                </div>
              </div>
            </div>
          </div>
          <style jsx>{`
            .marquee-container {
              width: 100%;
              overflow: hidden;
            }
            
            .marquee-content {
              display: flex;
              animation: scroll-right-to-left 15s linear infinite;
            }
            
            .marquee-text {
              white-space: nowrap;
              color: #ef4444;
              font-size: 1.5rem;
              font-weight: 600;
              letter-spacing: 0.05em;
              padding-right: 2rem;
            }
            
            @keyframes scroll-right-to-left {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-50%);
              }
            }
            
            @media (max-width: 768px) {
              .marquee-content {
                animation: scroll-right-to-left 10s linear infinite;
              }
              .marquee-text {
                font-size: 1.25rem;
              }
            }
            
            @media (max-width: 640px) {
              .marquee-content {
                animation: scroll-right-to-left 8s linear infinite;
              }
              .marquee-text {
                font-size: 1.125rem;
              }
            }
          `}</style>
        </section>

        {/* Why Work With Me Section */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Why Work <span className="text-red-500">With Me?</span></h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center group">
                <div className="w-20 h-20 mx-auto mb-6 bg-red-600/20 rounded-full flex items-center justify-center group-hover:bg-red-600/30 transition-colors">
                  <i className="ri-shield-check-line text-3xl text-red-500"></i>
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-red-400 transition-colors">Licensed Sales Representative</h3>
                <p className="text-slate-300">Fully licensed and certified professional with Royal LePage Crown Realty Services</p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 mx-auto mb-6 bg-red-600/20 rounded-full flex items-center justify-center group-hover:bg-red-600/30 transition-colors">
                  <i className="ri-time-line text-3xl text-red-500"></i>
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-red-400 transition-colors">35+ Years Experience</h3>
                <p className="text-slate-300">Proven track record with decades of successful transactions and satisfied clients</p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 mx-auto mb-6 bg-red-600/20 rounded-full flex items-center justify-center group-hover:bg-red-600/30 transition-colors">
                  <i className="ri-vip-line text-3xl text-red-500"></i>
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-red-400 transition-colors">Exclusive Listings</h3>
                <p className="text-slate-300">Access to exclusive properties and off-market opportunities</p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 mx-auto mb-6 bg-red-600/20 rounded-full flex items-center justify-center group-hover:bg-red-600/30 transition-colors">
                  <i className="ri-customer-service-2-line text-3xl text-red-500"></i>
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-red-400 transition-colors">Personalized Service</h3>
                <p className="text-slate-300">Direct, personal attention with regular updates via text, email, and phone</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section - Calendly Integration */}
        <section id="contact" className="py-20 bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Schedule Your <span className="text-red-500">Consultation</span></h2>
              <p className="text-xl text-slate-300">Book a 30-minute consultation to discuss your real estate needs</p>
            </div>

            {/* Calendly Integration - Full Width */}
            <div className="max-w-4xl mx-auto">
              <div className="bg-slate-900/50 p-8 rounded-xl border border-slate-700">
                <h3 className="text-2xl font-bold mb-6 text-red-400 text-center">Book Your Appointment</h3>
                <div className="bg-white rounded-lg overflow-hidden" style={{ height: '700px' }}>
                  <iframe
                    src="https://calendly.com/tjcmonteiro/free-mortgage-consultation-with-tony-monteiro"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    title="Schedule a consultation with Tony Monteiro"
                  ></iframe>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-slate-400 text-sm mb-4">
                    Can't see the calendar? <a href="https://calendly.com/tjcmonteiro/free-mortgage-consultation-with-tony-monteiro" target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300 transition-colors cursor-pointer">Click here to book directly</a>
                  </p>
                  
                  {/* Contact Information Below Calendly */}
                  <div className="grid md:grid-cols-2 gap-6 mt-8 pt-8 border-t border-slate-700">
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto mb-3 bg-red-600/20 rounded-lg flex items-center justify-center">
                        <i className="ri-phone-line text-red-500"></i>
                      </div>
                      <h4 className="font-semibold mb-2 text-red-400">Phone</h4>
                      <p className="text-slate-300 text-sm">(519) 658-3026</p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto mb-3 bg-red-600/20 rounded-lg flex items-center justify-center">
                        <i className="ri-map-pin-line text-red-500"></i>
                      </div>
                      <h4 className="font-semibold mb-2 text-red-400">Office</h4>
                      <p className="text-slate-300 text-sm">4-471 Hespeler Road</p>
                      <p className="text-slate-300 text-sm">Cambridge, ON N1R 6J2</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 border-t border-slate-700 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="flex items-center space-x-2 sm:space-x-4 mb-4">
                  <div className="bg-white rounded-lg p-2">
                    <img 
                      src="https://static.readdy.ai/image/9d1a81acaff8e51df20fb8b8c1aae197/41ba4e2750ccf943b2442aa568081ca1.png"
                      alt="Royal LePage Logo"
                      className="h-8 sm:h-12 w-auto object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold">Tony Monteiro</h3>
                    <p className="text-xs sm:text-sm text-slate-400">Sales Representative</p>
                  </div>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Your trusted partner in real estate with 35 years of experience serving the Waterloo region.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-lg mb-4 text-red-400">Quick Links</h4>
                <ul className="space-y-2 text-slate-400">
                  <li><a href="#home" className="hover:text-red-400 transition-colors cursor-pointer">Home</a></li>
                  <li><a href="#services" className="hover:text-red-400 transition-colors cursor-pointer">Services</a></li>
                  <li><a href="#about" className="hover:text-red-400 transition-colors cursor-pointer">About</a></li>
                  <li><a href="#contact" className="hover=text-red-400 transition-colors cursor-pointer">Contact</a></li>
                  <li><a href="https://www.royallepage.ca/en/realestate/privacy-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-red-400 transition-colors cursor-pointer">Privacy</a></li>
                  <li><a href="https://www.royallepage.ca/en/realestate/legal-notice/" target="_blank" rel="noopener noreferrer" className="hover:text-red-400 transition-colors cursor-pointer">Legal</a></li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-lg mb-4 text-red-400">Brokerage</h4>
                <div className="text-slate-400 text-sm space-y-1">
                  <p className="font-medium">Royal LePage Crown Realty Services</p>
                  <p>4-471 Hespeler Road</p>
                  <p>Cambridge, ON N1R 6J2</p>
                  <p>Phone: (519) 658-3026</p>
                  <p>Brokerage: (519) 621-2000</p>
                  <p>Fax: (519) 740-6403</p>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-700 pt-8 text-center text-slate-400 text-xs sm:text-sm">
              <p>&copy; 2025 Tony Monteiro, Sales Representative. Royal LePage Crown Realty Services. All rights reserved.</p>
              <div className="mt-4 flex justify-center items-center">
                <div className="bg-slate-800 px-8 py-4 rounded-lg border border-slate-600">
                  <span className="text-slate-300 text-sm">Website by </span>
                  <a 
                    href="https://www.pockitt.info/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-red-400 hover:text-red-300 transition-colors cursor-pointer font-bold text-lg"
                  >
                    Pockitt
                  </a>
                  <span className="text-slate-500 text-sm ml-2">• Design • Development • Hosting</span>
                </div>
              </div>
            </div>

          </div>
        </footer>

        {/* Consultation Modal */}
        {isConsultationModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-700">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-red-400">Schedule a Consultation</h3>
                  <button
                    onClick={() => setIsConsultationModalOpen(false)}
                    className="text-slate-400 hover:text-white cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
              </div>
              <form id="consultation-form" data-readdy-form onSubmit={handleConsultationSubmit} className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={consultationFormData.name}
                    onChange={(e) => setConsultationFormData({...consultationFormData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:border-red-500 focus:outline-none text-sm"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={consultationFormData.email}
                    onChange={(e) => setConsultationFormData({...consultationFormData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:border-red-5"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={consultationFormData.phone}
                    onChange={(e) => setConsultationFormData({...consultationFormData, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:border-red-5"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Service Interest</label>
                  <div className="relative">
                    <select
                      name="service"
                      value={consultationFormData.service}
                      onChange={(e) => setConsultationFormData({...consultationFormData, service: e.target.value})}
                      className="w-full px-4 py-3 pr-8 bg-slate-700 border border-slate-600 rounded-lg focus:border-red-5 appearance-none"
                    >
                      <option value="residential">Residential</option>
                      <option value="multi-family">Multi-Family</option>
                      <option value="commercial">Commercial</option>
                      <option value="investment">Investment Properties</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none"></i>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Additional Details</label>
                  <textarea
                    name="message"
                    value={consultationFormData.message}
                    onChange={(e) => setConsultationFormData({...consultationFormData, message: e.target.value})}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:border-red-400 focus:outline-none text-sm resize-none"
                    placeholder="Tell me about your real estate needs..."
                  ></textarea>
                  <div className="text-xs text-slate-400 mt-1">{consultationFormData.message.length}/500 characters</div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white py-3 px-6 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Scheduling...' : 'Schedule Consultation'}
                </button>
                {submitStatus === 'success' && (
                  <div className="mt-4 p-3 bg-green-600/20 border border-green-500 rounded-lg text-green-400 text-sm">
                    Consultation request sent! I'll contact you within 24 hours.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="mt-4 p-3 bg-red-600/20 border border-red-500 rounded-lg text-red-400 text-sm">
                    Failed to schedule consultation. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>
        )}

        {/* Message Modal */}
        {isMessageModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-700">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-red-400">Send a Message</h3>
                  <button
                    onClick={() => setIsMessageModalOpen(false)}
                    className="text-slate-400 hover:text-white cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
              </div>
              <form id="message-form" data-readdy-form onSubmit={handleMessageSubmit} className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={messageFormData.name}
                      onChange={(e) => setMessageFormData({...messageFormData, name: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:border-red-5"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={messageFormData.email}
                      onChange={(e) => setMessageFormData({...messageFormData, email: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:border-red-5"
                      required
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={messageFormData.phone}
                    onChange={(e) => setMessageFormData({...messageFormData, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:border-red-5"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={messageFormData.subject}
                    onChange={(e) => setMessageFormData({...messageFormData, subject: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:border-red-5"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={messageFormData.message}
                    onChange={(e) => setMessageFormData({...messageFormData, message: e.target.value})}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:border-red-5 resize-none"
                    required
                  ></textarea>
                  <div className="text-xs text-slate-400 mt-1">{messageFormData.message.length}/500 characters</div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-7 70 disabled:bg-red-600/50 text-white py-3 px-6 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                {submitStatus === 'success' && (
                  <div className="mt-4 p-3 bg-green-600/20 border border-green-500 rounded-lg text-green-400 text-sm">
                    Message sent successfully! I'll get back to you soon.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="mt-4 p-3 bg-red-600/20 border border-red-500 rounded-lg text-red-400 text-sm">
                    Failed to send message. Please try again.
                  </div>
                )}
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
